<template>
    <div class="tnb" :class="{ 'tnb-active': status?.status === 'training', 'tnb-disabled': disabled }">
        <div class="tnb-info">
            <span v-if="title" class="tnb-title">{{ title }}</span>
            <span class="tnb-count">
                <strong>{{ status?.labelsReady ?? 0 }}</strong>
                {{ countNoun }} ready for training
            </span>
            <span class="tnb-meta" v-if="lastTrainedDisplay">
                · last trained {{ lastTrainedDisplay }}
            </span>
            <span class="tnb-status" v-if="status?.status === 'training'">· training in progress…</span>
            <span class="tnb-status tnb-failed" v-else-if="status?.status === 'failed'">· last run FAILED</span>
            <span class="tnb-status tnb-disabled-note" v-if="disabledNote">· {{ disabledNote }}</span>
        </div>
        <div class="tnb-actions">
            <button class="tnb-btn-secondary" @click="$emit('refresh')" :disabled="loading" title="Refresh status">
                ↻
            </button>
            <button
                class="tnb-btn"
                @click="$emit('train')"
                :disabled="status?.status === 'training' || triggering || disabled"
            >
                {{ triggering ? 'Starting…' : 'Train Now' }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface ModelStatus {
    status: string
    lastTrained: string | null
    labelsReady: number
    metrics: any
}

const props = defineProps<{
    // Drives the count noun ("corrections" for price, "labels" for label-driven
    // models, "click-eligible deals" for the Clicky/CTR niche model). The
    // parent decides which model this bar refers to and emits the matching
    // train action.
    kind: 'price' | 'niche' | 'label' | 'pair'
    status: ModelStatus | null | undefined
    loading: boolean
    triggering: boolean
    title?: string
    disabled?: boolean
    disabledNote?: string
}>()
defineEmits<{ (e: 'train'): void; (e: 'refresh'): void }>()

const countNoun = computed(() => {
    if (props.kind === 'price') return 'corrections'
    if (props.kind === 'niche') return 'click-eligible deals'
    if (props.kind === 'pair') return 'pairs'
    return 'labels'
})

const lastTrainedDisplay = computed(() => {
    if (!props.status?.lastTrained) return null
    const d = new Date(props.status.lastTrained)
    if (isNaN(d.getTime())) return null
    const now = Date.now()
    const ageSec = Math.max(0, (now - d.getTime()) / 1000)
    if (ageSec < 60) return 'just now'
    if (ageSec < 3600) return `${Math.round(ageSec / 60)}m ago`
    if (ageSec < 86400) return `${Math.round(ageSec / 3600)}h ago`
    return d.toLocaleString('en-US', { month: 'short', day: 'numeric' })
})
</script>

<style scoped>
.tnb {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 14px;
    background: #f0f7ff;
    border: 1px solid #8ab4f0;
    border-radius: 6px;
    margin: 0 0 12px 0;
    font-size: 13px;
    flex-wrap: wrap;
}
.tnb-active { background: #fff8e1; border-color: #ffd54f; }
.tnb-disabled { background: #f5f5f5; border-color: #cfd8dc; opacity: 0.85; }

.tnb-info { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; color: #1565c0; }
.tnb-title { font-weight: 700; color: #0d47a1; margin-right: 4px; }
.tnb-count strong { font-size: 16px; color: #0d47a1; margin-right: 2px; }
.tnb-meta { color: #666; font-size: 12px; }
.tnb-status { color: #6b4f00; font-weight: 600; }
.tnb-status.tnb-failed { color: #c62828; }
.tnb-status.tnb-disabled-note { color: #607d8b; font-weight: 500; font-style: italic; }

.tnb-actions { display: flex; gap: 6px; }
.tnb-btn {
    padding: 5px 14px;
    background: #1565c0;
    color: #fff;
    border: none;
    border-radius: 4px;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
}
.tnb-btn:hover:not(:disabled) { background: #0d47a1; }
.tnb-btn:disabled { opacity: 0.6; cursor: not-allowed; }

.tnb-btn-secondary {
    padding: 5px 10px;
    background: #fff;
    color: #555;
    border: 1px solid #cfd8dc;
    border-radius: 4px;
    font-size: 13px;
    cursor: pointer;
}
.tnb-btn-secondary:hover:not(:disabled) { background: #f5f5f5; }
.tnb-btn-secondary:disabled { opacity: 0.6; cursor: not-allowed; }
</style>
